import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productService } from '../services/api';
import { supabase } from '../services/supabase';
import ProductCard from '../components/ProductCard';
import FeaturedCards3D from '../components/FeaturedCards3D';
import Spinner from '../components/Spinner';

const HomePage = ({ onCartUpdate }) => {
  const [products, setProducts] = useState([]);
  const [sliders, setSliders] = useState([]);
  const [telegramUrl, setTelegramUrl] = useState('https://t.me/iwantcheatsk');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadProducts();
    loadSliders();
    loadTelegramLink();
  }, []);

  const loadTelegramLink = async () => {
    try {
      const { data } = await supabase
        .from('store_settings')
        .select('telegram_url, telegram_username')
        .limit(1)
        .single();

      if (data?.telegram_url) {
        setTelegramUrl(data.telegram_url);
        return;
      }

      if (data?.telegram_username) {
        setTelegramUrl(`https://t.me/${data.telegram_username.replace('@', '')}`);
      }
    } catch (err) {
      // Keep fallback link when settings are unavailable
    }
  };

  const loadProducts = async () => {
    try {
      const response = await productService.getAll();
      setProducts(response.data.products);
    } catch (err) {
      setError('Failed to load products');
    }
  };

  const loadSliders = async () => {
    try {
      const { data, error } = await supabase
        .from('sliders')
        .select('*')
        .eq('active', true)
        .order('display_no', { ascending: true });

      if (error) throw error;
      setSliders(data || []);
      setLoading(false);
    } catch (err) {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existing = cart.find(item => item.productId === product.productId);

    if (existing) {
      existing.quantity += 1;
    } else {
      cart.push({ ...product, quantity: 1 });
    }

    localStorage.setItem('cart', JSON.stringify(cart));
    if (onCartUpdate) onCartUpdate();
  };

  if (loading) return (
    <div className="loading">
      <Spinner size="40px" color="#eb6f09" />
      <p style={{ marginTop: '16px', color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>Loading products...</p>
    </div>
  );

  if (error) return <div className="error">{error}</div>;

  return (
    <div className="home-page">

      {/* ── Hero ── */}
      <section className="hero-section">
        <a href={telegramUrl} target="_blank" rel="noopener noreferrer">
          <div className="hero-badge">
            <span className="hero-badge-dot"></span>
            Trusted by 70K+ gamers · 5★ rated · since 2024
          </div>
        </a>

        <h1 className="hero-title">IWantCheats</h1>
        <p className="hero-tagline">By Gamers, For Gamers</p>
        <p className="hero-desc">
          Elevate your gameplay with our premium undetected cheats &amp; HWID spoofers.
          The #1 private provider since 2014.
        </p>

        <div className="hero-btns">
          <a href={telegramUrl} target="_blank" rel="noopener noreferrer" className="btn-primary">
            <span>
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21.5 3.5 2.8 10.9c-.9.4-.8 1.7.2 2l4.7 1.5 1.8 5.5c.3.9 1.5 1.1 2.1.3l2.7-3.4 4.9 3.6c.8.6 1.9.1 2.1-.9L23.4 5c.2-1-1-1.8-1.9-1.5Z" />
              </svg>
              Join Telegram
            </span>
          </a>
          <a href="#products" className="btn-accent">
            <span>
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                <line x1="3" x2="21" y1="6" y2="6"></line>
                <path d="M16 10a4 4 0 0 1-8 0"></path>
              </svg>
              Browse Cheats
            </span>
          </a>
        </div>
      </section>

      {/* ── Marquee 1 ── */}
      <div className="marquee-container">
        <div className="marquee">
          <span>🎮 Premium Game Cheats &nbsp;•&nbsp; Undetected &amp; Safe &nbsp;•&nbsp; Regular Updates &nbsp;•&nbsp; Lifetime Support &nbsp;•&nbsp; 70K+ Gamers Trusted &nbsp;•&nbsp; 🎮 Premium Game Cheats &nbsp;•&nbsp; Undetected &amp; Safe &nbsp;•&nbsp; Regular Updates &nbsp;•&nbsp; Lifetime Support &nbsp;•&nbsp; 70K+ Gamers Trusted &nbsp;•&nbsp;</span>
        </div>
      </div>

      {/* ── Featured Slider (kept from original) ── */}
      {sliders.length > 0 && (
        <section className="featured-section" style={{ padding: '40px 20px 0', maxWidth: 1200, margin: '0 auto' }}>
          <div className="section-header" style={{ textAlign: 'center', marginBottom: 20 }}>
            <div className="section-tag">⚡ Featured</div>
          </div>
          <FeaturedCards3D
            sliders={sliders}
            onCardClick={(slider) => {
              if (slider.redirect_url) {
                if (slider.redirect_url.startsWith('http')) {
                  window.open(slider.redirect_url, '_blank');
                } else {
                  navigate(slider.redirect_url);
                }
              }
            }}
          />
        </section>
      )}

      {/* ── Popular Games ── */}
      {false && (
      <div className="games-section" style={{ paddingTop: 48 }}>
        <div className="section-header" style={{ textAlign: 'center', marginBottom: 24 }}>
          <div className="section-tag">🎮 Popular Games</div>
          <h2 className="section-title">Popular Game Cheats</h2>
        </div>
        <div className="games-grid">
          <a href="/product/arc-raiders-cheats" className="game-link">Arc Raiders</a>
          <a href="/product/rust-cheats" className="game-link">Rust</a>
          <a href="/product/apex-legends-cheats" className="game-link">Apex Legends</a>
          <a href="/product/delta-force-cheats" className="game-link">Delta Force</a>
          <a href="/product/Marathon-Cheats" className="game-link">Marathon</a>
          <a href="/product/fortnite-cheats" className="game-link">Fortnite</a>
          <a href="/product/gray-zone-warfare-cheats" className="game-link">Gray Zone</a>
          <a href="/product/dayz-cheats" className="game-link">DayZ</a>
          <a href="/product/black-ops-7-cheats" className="game-link">BO7 / BO6</a>
          <a href="/product/battlefield-6-cheats" className="game-link">Battlefield 6</a>
          <a href="/product/valorant-cheats" className="game-link">Valorant</a>
          <a href="#products" className="game-link" style={{ color: 'var(--accent)' }}>View All →</a>
        </div>
      </div>
      )}

      {/* ── Marquee 2 ── */}
      <div className="marquee-container marquee-secondary" style={{ marginTop: 40 }}>
        <div className="marquee marquee-reverse">
          <span>⚡ Instant Delivery &nbsp;•&nbsp; 🔒 Secure Payments &nbsp;•&nbsp; 24/7 Support &nbsp;•&nbsp; 💎 Best Prices &nbsp;•&nbsp; ✅ Trusted by Thousands &nbsp;•&nbsp; ⚡ Instant Delivery &nbsp;•&nbsp; 🔒 Secure Payments &nbsp;•&nbsp; 24/7 Support &nbsp;•&nbsp; 💎 Best Prices &nbsp;•&nbsp; ✅ Trusted by Thousands &nbsp;•&nbsp;</span>
        </div>
      </div>

      {/* ── Why Choose Us ── */}
      <div className="section-wrapper">
        <div className="section-header">
          <div className="section-tag">✅ Benefits</div>
          <h2 className="section-title">Why Choose IWantCheats?</h2>
          <p className="section-sub">Trusted by 70,000+ gamers since 2014</p>
        </div>
        <div className="features-grid">
          <div className="feature-card">
            <span className="feature-icon">🛡️</span>
            <div className="feature-title">Fully Undetected</div>
            <p className="feature-desc">Bypasses EAC, BattlEye, Vanguard &amp; Ricochet. Tested daily for safety.</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon">⚡</span>
            <div className="feature-title">Instant Delivery</div>
            <p className="feature-desc">License key delivered instantly after payment. Start within minutes.</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon">🔒</span>
            <div className="feature-title">HWID Spoofer</div>
            <p className="feature-desc">Full hardware ban protection. Game safely on any hardware.</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon">📺</span>
            <div className="feature-title">Streamproof</div>
            <p className="feature-desc">100% invisible on OBS and all streaming software.</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon">💎</span>
            <div className="feature-title">Premium Features</div>
            <p className="feature-desc">Aimbot, ESP, radar, no recoil and more — best in class.</p>
          </div>
          <div className="feature-card">
            <span className="feature-icon">🌐</span>
            <div className="feature-title">24/7 Support</div>
            <p className="feature-desc">Expert support available on Discord around the clock.</p>
          </div>
        </div>
      </div>

      {/* ── Products Grid ── */}
      <div id="products" style={{ padding: '0 20px', maxWidth: 1200, margin: '0 auto' }}>
        <div className="section-header" style={{ marginBottom: 28, textAlign: 'center' }}>
          <div className="section-tag">🏆 Best Sellers</div>
          <h2 className="section-title">Best Seller Products</h2>
          <p className="section-sub">Loved by our customers</p>
        </div>

        <div className="products-grid">
          {products.map(product => (
            <ProductCard
              key={product.productId}
              product={product}
              onAddToCart={addToCart}
              onViewDetails={() => navigate(`/product/${product.slug || product.productId}`)}
            />
          ))}
        </div>
      </div>

    </div>
  );
};

export default HomePage;
